import { isDiagnosisActiveDuringPeriod } from './patientDiagnosisService';
import { ymdLocal } from './rpmCensusUtils';

/** Age in whole years on `asOf` from a `YYYY-MM-DD` string or Firestore Timestamp/Date. */
function ageFromDob(dob, asOf) {
  if (!dob) return null;
  const s =
    typeof dob === 'string'
      ? dob
      : dob?.toDate?.()?.toISOString?.()?.slice(0, 10) ||
        (dob instanceof Date ? dob.toISOString().slice(0, 10) : null);
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  const birth = new Date(y, m - 1, d);
  const ref = asOf || new Date();
  let age = ref.getFullYear() - birth.getFullYear();
  if (ref.getMonth() < birth.getMonth() || (ref.getMonth() === birth.getMonth() && ref.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
}

function ageGroupLabel(age) {
  if (age == null) return 'Unknown';
  if (age <= 17) return '0-17';
  if (age <= 24) return '18-24';
  if (age <= 44) return '25-44';
  if (age <= 64) return '45-64';
  return '65+';
}

const AGE_ORDER = ['0-17', '18-24', '25-44', '45-64', '65+', 'Unknown'];

function normalizeGender(g) {
  const s = String(g || '').trim().toLowerCase();
  if (!s) return 'Unknown';
  if (s.startsWith('m')) return 'Male';
  if (s.startsWith('f')) return 'Female';
  return 'Other / Unknown';
}

/** Distinct condition category labels active at any point in the period (deduped). */
function activeConditionCategories(patient, startYmd, endYmd) {
  const out = new Set();
  (patient.diagnoses || []).forEach((dx) => {
    if (!isDiagnosisActiveDuringPeriod(dx, startYmd, endYmd)) return;
    const label = (dx.category || dx.icdCode || '').toString().trim();
    if (label) out.add(label);
  });
  return out;
}

/**
 * Build the Disease & Demographics dataset for a period. The caller supplies the
 * cohort (RPM-active-during-period patients, see rpmPeriodCohort.js); conditions
 * count when active on any day of the period, and age is age on the last day of
 * the period, so re-running a past period gives the same figures.
 *
 * @param {Array<object>} patients full patient docs (gender, dateOfBirth, address*, diagnoses[])
 * @param {{ period?: { start: Date, end: Date } }} [opts]
 */
export function buildDiseaseDemographicsReport(patients, { period } = {}) {
  const list = Array.isArray(patients) ? patients : [];
  const endDate = period?.end || new Date();
  const startYmd = ymdLocal(period?.start || endDate);
  const endYmd = ymdLocal(endDate);

  const conditionCounts = new Map();
  const genderCounts = new Map();
  const ageCounts = new Map(AGE_ORDER.map((k) => [k, 0]));
  const comorbidityCounts = new Map([
    ['0', 0],
    ['1', 0],
    ['2', 0],
    ['3', 0],
    ['4+', 0],
  ]);
  const locationMap = new Map();

  let totalActiveDx = 0;
  let conditionsSum = 0;
  let multimorbid = 0;

  list.forEach((p) => {
    const cats = activeConditionCategories(p, startYmd, endYmd);
    cats.forEach((label) => conditionCounts.set(label, (conditionCounts.get(label) || 0) + 1));
    const n = cats.size;
    totalActiveDx += n;
    conditionsSum += n;
    if (n >= 2) multimorbid += 1;
    const comorbKey = n >= 4 ? '4+' : String(n);
    comorbidityCounts.set(comorbKey, (comorbidityCounts.get(comorbKey) || 0) + 1);

    const genderLabel = normalizeGender(p.gender);
    genderCounts.set(genderLabel, (genderCounts.get(genderLabel) || 0) + 1);

    const grp = ageGroupLabel(ageFromDob(p.dateOfBirth, endDate));
    ageCounts.set(grp, (ageCounts.get(grp) || 0) + 1);

    const zip = String(p.addressZip || '').trim().slice(0, 5);
    if (zip) {
      if (!locationMap.has(zip)) {
        locationMap.set(zip, {
          zip,
          city: (p.addressCity || '').trim(),
          state: (p.addressState || '').trim(),
          count: 0,
        });
      }
      locationMap.get(zip).count += 1;
    }
  });

  const total = list.length;

  const conditions = [...conditionCounts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);

  const gender = [...genderCounts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);

  const ageGroups = AGE_ORDER.map((label) => ({ label, count: ageCounts.get(label) || 0 })).filter(
    (g) => g.label !== 'Unknown' || g.count > 0,
  );

  const comorbidity = ['0', '1', '2', '3', '4+'].map((label) => ({
    label,
    count: comorbidityCounts.get(label) || 0,
  }));

  const locations = [...locationMap.values()].sort((a, b) => b.count - a.count);

  return {
    totalPatients: total,
    totalActiveDx,
    avgConditions: total ? conditionsSum / total : 0,
    pctMultimorbid: total ? Math.round((100 * multimorbid) / total) : null,
    distinctZips: locations.length,
    conditions,
    topConditions: conditions.slice(0, 12),
    gender,
    ageGroups,
    comorbidity,
    locations,
  };
}
